import { useTranslation } from 'react-i18next';
import { Download, Plug, RefreshCw, Upload } from 'lucide-react';
import { Button } from '@bliks-io/ui';
import {
  ExcelSourceRow,
  PersonioSourceRow
} from '@/pages/organization-settings/data-sync/SourceRows';
import type { usePersonioImport } from '../usePersonioImport';
import { CollapsibleFieldExplainer } from './CollapsibleFieldExplainer';
import { OrAddManually } from './OrAddManually';
import type { ExplainerSection } from './FieldExplainer';

interface SetupSourceChoiceProps {
  downloadTemplate: () => void;
  onUpload: () => void;
  onAddManually: () => void;
  /** i18n key of the manual-add action, named after the entity */
  addLabelKey: string;
  explainerHeadingKey: string;
  explainerSections: ExplainerSection[];
  personio: ReturnType<typeof usePersonioImport>;
}

/**
 * The empty state of the first step: Excel or Personio, in the rows of the
 * Datenabgleich settings page, with the manual row as the quiet alternative.
 */
export function SetupSourceChoice({
  downloadTemplate,
  onUpload,
  onAddManually,
  addLabelKey,
  explainerHeadingKey,
  explainerSections,
  personio
}: SetupSourceChoiceProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-3">
      <ExcelSourceRow
        actions={
          <>
            <Button variant="secondary" size="small" onClick={downloadTemplate}>
              <Download className="h-4 w-4" />
              {t('common.excelUpload.downloadTemplateTitle')}
            </Button>
            <Button variant="primary" size="small" onClick={onUpload}>
              <Upload className="h-4 w-4" />
              {t('dataSync.settings.uploadExcel')}
            </Button>
          </>
        }
      />

      <PersonioSourceRow
        connected={personio.isLinked}
        actions={
          personio.isLinked ? (
            <>
              <Button
                variant="primary"
                size="small"
                loading={personio.isLoading}
                disabled={personio.isUnlinking}
                onClick={personio.load}
              >
                <RefreshCw className="h-4 w-4" />
                {t('initialSetup.personio.load')}
              </Button>
              <Button
                variant="ghost"
                size="small"
                loading={personio.isUnlinking}
                disabled={personio.isLoading}
                onClick={personio.unlink}
              >
                {t('dataSync.settings.unlink')}
              </Button>
            </>
          ) : (
            <Button variant="secondary" size="small" onClick={personio.connect}>
              <Plug className="h-4 w-4" />
              {t('dataSync.settings.connect')}
            </Button>
          )
        }
      />

      <OrAddManually onAddManually={onAddManually} addLabelKey={addLabelKey} className="pt-1" />

      <CollapsibleFieldExplainer headingKey={explainerHeadingKey} sections={explainerSections} />
    </div>
  );
}
